<template>
  <div class="container-fluid">
    <div class="page-title"><h3>Crear paquete</h3></div>

    <div class="card mb-3">
      <div class="card-body wizard-steps">
        <div class="wizard-step" :class="{ active: step === 1, done: step > 1 }">1. Destino y fecha</div>
        <div class="wizard-step" :class="{ active: step === 2, done: step > 2 }">2. Seleccionar viaje</div>
        <div v-if="isFlexibleTrip" class="wizard-step" :class="{ active: step === 3, done: step > 3 }">
          3. Dirección entrega
        </div>
        <div class="wizard-step" :class="{ active: step === detailsStep, done: step > detailsStep }">
          {{ detailsStep }}. Detalle y paquete
        </div>
      </div>
    </div>

    <div v-if="step === 1" class="card mb-3">
      <div class="card-body">
        <div class="row g-3 align-items-end">
          <div class="col-md-5">
            <label class="form-label">De donde</label>
            <input v-model.trim="searchForm.origen" class="form-control" @input="onAddressInput('origen')" />
            <div v-if="showOrigenSuggestions" class="suggestions-list mt-1">
              <button
                v-for="item in originSuggestions"
                :key="item.place_name"
                type="button"
                class="suggestion-item"
                @click="selectSuggestion('origen', item)"
              >
                {{ item.place_name }}
              </button>
            </div>
          </div>
          <div class="col-md-5">
            <label class="form-label">A donde</label>
            <input v-model.trim="searchForm.destino" class="form-control" @input="onAddressInput('destino')" />
            <div v-if="showDestinoSuggestions" class="suggestions-list mt-1">
              <button
                v-for="item in destinationSuggestions"
                :key="item.place_name"
                type="button"
                class="suggestion-item"
                @click="selectSuggestion('destino', item)"
              >
                {{ item.place_name }}
              </button>
            </div>
          </div>
          <div class="col-md-2">
            <label class="form-label">Fecha</label>
            <input v-model="searchForm.fecha" type="date" class="form-control" />
          </div>
        </div>
        <div class="text-end mt-3">
          <button class="btn btn-primary" :disabled="loadingTrips" @click="handleSearchStep">Buscar viajes</button>
        </div>
      </div>
    </div>

    <div v-if="step === 2" class="card mb-3">
      <div class="card-body">
        <button class="btn btn-outline-secondary btn-sm mb-2" @click="step = 1">Modificar busqueda</button>
        <div v-if="availableTrips.length === 0" class="text-muted">No hay viajes para los filtros seleccionados.</div>
        <div v-else class="trip-cards">
          <button
            v-for="trip in availableTrips"
            :key="trip.id"
            type="button"
            class="trip-card"
            :class="{ selected: selectedTrip?.id === trip.id }"
            @click="selectTrip(trip)"
          >
            <div><strong>{{ formatTime(trip.tripTime) }}</strong> - {{ formatDate(trip.tripDate) }}</div>
            <div class="small">{{ trip.originAddress }} -> {{ trip.destinationAddress }}</div>
            <div v-if="trip.deliveryFlexible" class="mt-1">
              <span class="badge bg-success">Entrega personalizada</span>
              <span class="small text-muted ms-2">Disponible</span>
            </div>
          </button>
        </div>
        <div class="text-end mt-3">
          <button class="btn btn-primary" :disabled="!selectedTrip" @click="goToNextAfterTripSelect">Continuar</button>
        </div>
      </div>
    </div>

    <div v-if="isFlexibleTrip && step === 3" class="card mb-3">
      <div class="card-body">
        <MapLocationPicker
          label="Dirección de entrega personalizada"
          subtitle="Selecciona el punto donde el viajero entregará el paquete (puede ser distinto al destino publicado)."
          :initialLatitude="deliveryLocation.latitude"
          :initialLongitude="deliveryLocation.longitude"
          :initialAddress="deliveryLocation.address"
          @confirm="onConfirmDeliveryLocation"
        />
        <div class="d-flex justify-content-between mt-3">
          <button class="btn btn-outline-secondary" @click="step = 2">Atrás</button>
          <button class="btn btn-primary" :disabled="!hasDeliveryLocation" @click="step = detailsStep">Continuar</button>
        </div>
      </div>
    </div>

    <div v-if="step === detailsStep" class="row">
      <div class="col-xl-7 mb-3">
        <div class="card">
          <div class="card-body">
            <h3>Información del viaje</h3>
            <div><strong>Origen:</strong> {{ selectedTrip?.originAddress }}</div>
            <div><strong>Destino:</strong> {{ selectedTrip?.destinationAddress }}</div>
            <div><strong>Fecha:</strong> {{ formatDate(selectedTrip?.tripDate) }}</div>
            <div><strong>Hora:</strong> {{ formatTime(selectedTrip?.tripTime) }}</div>
            <div ref="tripMapContainer" class="trip-map"></div>
            <div v-if="isFlexibleTrip && deliveryLocation.address" class="card mt-3 border">
              <div class="card-body py-3">
                <div class="d-flex justify-content-between align-items-center">
                  <div>
                    <div class="fw-semibold">Dirección de entrega personalizada</div>
                    <div class="small text-muted text-break">{{ deliveryLocation.address }}</div>
                  </div>
                  <span class="badge bg-success">Entrega personalizada</span>
                </div>
              </div>
            </div>
            <button class="btn btn-outline-primary btn-sm mt-2" @click="step = isFlexibleTrip ? 3 : 2">
              {{ isFlexibleTrip ? 'Cambiar entrega' : 'Cambiar viaje' }}
            </button>
            <button class="btn btn-outline-primary btn-sm mt-2 ms-2" @click="step = 2">Cambiar viaje</button>
          </div>
        </div>
      </div>
      <div class="col-xl-5 mb-3">
        <div class="card">
          <div class="card-body">
            <form @submit.prevent="onSubmit">
              <div class="row g-3 mb-2">
                <div class="col-12">
                  <label class="form-label">Modo de carga</label>
                  <select v-model="createMode" class="form-select">
                    <option value="single">Paquete único</option>
                    <option value="batch">Carga múltiple</option>
                  </select>
                </div>
              </div>

              <div v-if="createMode === 'single'" class="row g-3">
                <div class="col-md-6">
                  <label class="form-label">Nombre destinatario</label>
                  <input v-model.trim="form.recipientName" class="form-control" required />
                </div>
                <div class="col-md-6">
                  <label class="form-label">Telefono destinatario</label>
                  <input v-model.trim="form.recipientPhone" class="form-control" />
                </div>
                <div class="col-12">
                  <label class="form-label">Descripcion</label>
                  <input v-model.trim="form.description" class="form-control" required />
                </div>
                <div class="col-md-6">
                  <label class="form-label">Categoria</label>
                  <select v-model="form.category" class="form-select" @change="handleCategoryChange">
                    <option v-for="cat in categories" :key="cat.value" :value="cat.value">{{ cat.label }}</option>
                  </select>
                </div>
                <div class="col-md-6">
                  <label class="form-label">Peso (kg)</label>
                  <input v-model.number="form.weightKg" type="number" min="0.1" step="0.1" class="form-control" required />
                </div>
                <div v-if="isDocumentCategory()" class="col-12">
                  <label class="form-label">Tipo de documento</label>
                  <select v-model="selectedDocumentType" class="form-select" @change="handleDocumentTypeChange">
                    <option v-for="preset in documentSizePresets" :key="preset.value" :value="preset.value">
                      {{ preset.label }}
                    </option>
                  </select>
                </div>
                <div v-if="isDocumentCategory() && !manualDimensionsEnabled()" class="col-12">
                  <div class="alert alert-light border">
                    <div class="small">
                      Se aplicarán dimensiones sugeridas automáticamente:
                      <strong>{{ selectedDocumentPreset?.label }}</strong>.
                    </div>
                    <div class="small">
                      Largo: {{ selectedDocumentPreset?.lengthRange }} | Ancho: {{ selectedDocumentPreset?.widthRange }}
                    </div>
                  </div>
                </div>
                <div v-if="manualDimensionsEnabled()" class="col-md-4">
                  <label class="form-label">Largo (cm)</label>
                  <input v-model.number="form.lengthCm" type="number" min="1" step="0.1" class="form-control" required />
                </div>
                <div v-if="manualDimensionsEnabled()" class="col-md-4">
                  <label class="form-label">Ancho (cm)</label>
                  <input v-model.number="form.widthCm" type="number" min="1" step="0.1" class="form-control" required />
                </div>
                <div v-if="manualDimensionsEnabled()" class="col-md-4">
                  <label class="form-label">Alto (cm)</label>
                  <input v-model.number="form.heightCm" type="number" min="1" step="0.1" class="form-control" required />
                </div>
                <div class="col-md-6">
                  <label class="form-label">Valor declarado (MXN)</label>
                  <input v-model.number="form.declaredValueMxn" type="number" min="0" step="0.01" class="form-control" />
                </div>
                <div class="col-md-6 d-flex align-items-end">
                  <div class="form-check mb-2">
                    <input id="urgency" v-model="form.urgency" class="form-check-input" type="checkbox" />
                    <label class="form-check-label" for="urgency">Urgencia</label>
                  </div>
                </div>
                <div class="col-12">
                  <label class="form-label">Instrucciones especiales de entrega.</label>
                  <textarea v-model.trim="form.specialInstructions" rows="2" class="form-control"></textarea>
                </div>
                <div class="col-12">
                  <div class="form-check">
                    <input id="packagingConfirmed" v-model="form.packagingConfirmed" class="form-check-input" type="checkbox" />
                    <label class="form-check-label" for="packagingConfirmed">Confirmación de empaque adecuado</label>
                  </div>
                </div>
                <div class="col-12">
                  <div class="form-check">
                    <input id="noProhibitedConfirmed" v-model="form.noProhibitedConfirmed" class="form-check-input" type="checkbox" />
                    <label class="form-check-label" for="noProhibitedConfirmed">Confirmo que mi paquete no contiene artículos prohibidos</label>
                  </div>
                </div>
                <div class="col-12">
                  <button type="button" class="btn btn-outline-primary btn-sm" @click="showForbiddenObjects">
                    Ver objetos prohibidos
                  </button>
                </div>
              </div>

              <div v-else class="row g-3">
                <div class="col-12">
                  <div class="form-check">
                    <input id="sameRecipient" v-model="batchSameRecipient" class="form-check-input" type="checkbox" />
                    <label class="form-check-label" for="sameRecipient">Mismo destinatario para todos</label>
                  </div>
                </div>
                <template v-if="batchSameRecipient">
                  <div class="col-md-6">
                    <label class="form-label">Nombre destinatario (común)</label>
                    <input v-model.trim="batchCommonRecipientName" class="form-control" required />
                  </div>
                  <div class="col-md-6">
                    <label class="form-label">Teléfono destinatario (común)</label>
                    <input v-model.trim="batchCommonRecipientPhone" class="form-control" />
                  </div>
                </template>

                <div class="col-12">
                  <div class="d-flex justify-content-between align-items-center mb-2">
                    <strong>Paquetes del lote</strong>
                    <button type="button" class="btn btn-outline-primary btn-sm" @click="addBatchItem">Agregar paquete</button>
                  </div>
                  <div v-for="(item, idx) in batchItems" :key="item.localId" class="border rounded p-3 mb-2">
                    <div class="d-flex justify-content-between align-items-center mb-2">
                      <span class="small fw-semibold">Paquete #{{ idx + 1 }}</span>
                      <button type="button" class="btn btn-outline-danger btn-sm" @click="removeBatchItem(idx)" :disabled="batchItems.length === 1">Eliminar</button>
                    </div>
                    <div class="row g-2">
                      <template v-if="!batchSameRecipient">
                        <div class="col-md-6">
                          <label class="form-label small mb-0">Nombre destinatario</label>
                          <input v-model.trim="item.recipientName" class="form-control" placeholder="Destinatario" required />
                        </div>
                        <div class="col-md-6">
                          <label class="form-label small mb-0">Teléfono destinatario</label>
                          <input v-model.trim="item.recipientPhone" class="form-control" placeholder="Teléfono" />
                        </div>
                      </template>
                      <div class="col-12">
                        <label class="form-label small mb-0">Descripción del contenido</label>
                        <input v-model.trim="item.description" class="form-control" placeholder="Ej. Acta de nacimiento, contrato, etc." required />
                      </div>
                      <div class="col-md-6">
                        <label class="form-label small mb-0">Categoría (tipo de paquete)</label>
                        <select v-model="item.category" class="form-select" @change="handleBatchItemCategoryChange(item)">
                          <option v-for="cat in categories" :key="cat.value" :value="cat.value">{{ cat.label }}</option>
                        </select>
                      </div>
                      <div class="col-md-6">
                        <label class="form-label small mb-0">Peso (kg)</label>
                        <input v-model.number="item.weightKg" type="number" min="0.1" step="0.1" class="form-control" required />
                      </div>
                      <div v-if="isBatchDocumentCategory(item)" class="col-12">
                        <label class="form-label small mb-0">Tipo de documento</label>
                        <select v-model="item.selectedDocumentType" class="form-select" @change="handleBatchItemDocumentTypeChange(item)">
                          <option v-for="preset in documentSizePresets" :key="preset.value" :value="preset.value">
                            {{ preset.label }}
                          </option>
                        </select>
                      </div>
                      <div v-if="isBatchDocumentCategory(item) && !batchManualDimensionsEnabled(item)" class="col-12">
                        <div class="alert alert-light border py-2 mb-0">
                          <div class="small">
                            Dimensiones sugeridas:
                            <strong>{{ getBatchItemDocumentPreset(item)?.label }}</strong>
                          </div>
                          <div class="small text-muted">
                            Largo: {{ getBatchItemDocumentPreset(item)?.lengthRange }} · Ancho: {{ getBatchItemDocumentPreset(item)?.widthRange }}
                          </div>
                        </div>
                      </div>
                      <template v-if="batchManualDimensionsEnabled(item)">
                        <div class="col-md-4">
                          <label class="form-label small mb-0">Largo (cm)</label>
                          <input v-model.number="item.lengthCm" type="number" min="1" step="0.1" class="form-control" required />
                        </div>
                        <div class="col-md-4">
                          <label class="form-label small mb-0">Ancho (cm)</label>
                          <input v-model.number="item.widthCm" type="number" min="1" step="0.1" class="form-control" required />
                        </div>
                        <div class="col-md-4">
                          <label class="form-label small mb-0">Alto (cm)</label>
                          <input v-model.number="item.heightCm" type="number" min="1" step="0.1" class="form-control" required />
                        </div>
                      </template>
                      <div class="col-md-6">
                        <label class="form-label small mb-0">Valor declarado (MXN)</label>
                        <input v-model.number="item.declaredValueMxn" type="number" min="0" step="0.01" class="form-control" />
                      </div>
                      <div class="col-md-6 d-flex align-items-end">
                        <div class="form-check mb-2">
                          <input :id="`urgency-${item.localId}`" v-model="item.urgency" class="form-check-input" type="checkbox" />
                          <label class="form-check-label" :for="`urgency-${item.localId}`">Urgencia</label>
                        </div>
                      </div>
                      <div class="col-12">
                        <label class="form-label small mb-0">Instrucciones especiales de entrega (opcional)</label>
                        <textarea v-model.trim="item.specialInstructions" rows="2" class="form-control" placeholder="Instrucciones adicionales"></textarea>
                      </div>
                      <div class="col-12">
                        <div class="form-check">
                          <input :id="`pack-${item.localId}`" v-model="item.packagingConfirmed" class="form-check-input" type="checkbox" />
                          <label class="form-check-label" :for="`pack-${item.localId}`">Confirmación de empaque adecuado</label>
                        </div>
                      </div>
                      <div class="col-12">
                        <div class="form-check">
                          <input :id="`nop-${item.localId}`" v-model="item.noProhibitedConfirmed" class="form-check-input" type="checkbox" />
                          <label class="form-check-label" :for="`nop-${item.localId}`">Confirmo que mi paquete no contiene artículos prohibidos</label>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div class="mt-2">
                    <button type="button" class="btn btn-outline-primary btn-sm" @click="showForbiddenObjects">
                      Ver objetos prohibidos
                    </button>
                  </div>
                </div>
              </div>
              <div v-if="errorMsg" class="alert alert-danger mt-3">{{ errorMsg }}</div>
              <div v-if="successMsg" class="alert alert-success mt-3">{{ successMsg }}</div>
              <div class="text-end mt-3">
                <button type="submit" class="btn btn-primary" :disabled="loading || !selectedTrip">
                  {{ loading ? 'Publicando...' : createMode === 'batch' ? 'Publicar lote' : 'Publicar paquete' }}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Swal from 'sweetalert2';
import MapLocationPicker from '@/components/trips/MapLocationPicker.vue';
import origenMarkerImg from '@/assets/origen.png';
import destinoMarkerImg from '@/assets/destino.png';
import { packagesApi } from '@/api/packages';
import { forbiddenObjectsApi } from '@/api/forbidden-objects';
import { shippingRatesApi } from '@/api/shipping-rates';
import { tripsApi } from '@/api/trips';

export default {
  name: 'CrearPaquete',
  components: { MapLocationPicker },
  data() {
    return {
      step: 1,
      loading: false,
      loadingTrips: false,
      createMode: 'single',
      batchSameRecipient: false,
      batchCommonRecipientName: '',
      batchCommonRecipientPhone: '',
      batchItems: [],
      errorMsg: '',
      successMsg: '',
      availableTrips: [],
      selectedTrip: null,
      deliveryLocation: { latitude: null, longitude: null, address: '' },
      tripMap: null,
      tripMarkers: [],
      originSuggestions: [],
      destinationSuggestions: [],
      showOrigenSuggestions: false,
      showDestinoSuggestions: false,
      addressDebounceTimer: null,
      searchForm: { origen: '', destino: '', fecha: '' },
      form: {
        recipientName: '',
        recipientPhone: '',
        description: '',
        category: 'DOCUMENTOS',
        weightKg: 1,
        lengthCm: 10,
        widthCm: 10,
        heightCm: 10,
        declaredValueMxn: 0,
        urgency: false,
        specialInstructions: '',
        packagingConfirmed: false,
        noProhibitedConfirmed: false,
        offeredPriceMxn: null,
      },
      shippingRate: {
        baseFare: 40,
        perKm: 2.5,
        perKg: 8,
        perVolumeFactor: 0.0005,
        urgencySurcharge: 25,
      },
      selectedDocumentType: 'CARTA',
      documentSizePresets: [
        {
          value: 'CARTA',
          label: 'Tamaño carta',
          lengthRange: 'Entre 29 cm y 29.5 cm',
          widthRange: 'Entre 23.5 cm y 24 cm',
          lengthCm: 29.2,
          widthCm: 23.8,
          heightCm: 1,
        },
        {
          value: 'OFICIO',
          label: 'Tamaño oficio',
          lengthRange: 'Entre 34 cm y 35 cm',
          widthRange: 'Entre 21.5 cm y 22 cm',
          lengthCm: 34.5,
          widthCm: 21.8,
          heightCm: 1,
        },
        {
          value: 'A4',
          label: 'Tamaño A4',
          lengthRange: 'Entre 29.5 cm y 30 cm',
          widthRange: 'Entre 20.8 cm y 21.2 cm',
          lengthCm: 29.7,
          widthCm: 21,
          heightCm: 1,
        },
        {
          value: 'LEGAL',
          label: 'Tamaño legal',
          lengthRange: 'Entre 35.5 cm y 36 cm',
          widthRange: 'Entre 21.5 cm y 22 cm',
          lengthCm: 35.7,
          widthCm: 21.8,
          heightCm: 1,
        },
        {
          value: 'DIPLOMA',
          label: 'Diploma',
          lengthRange: 'Entre 30 cm y 35 cm',
          widthRange: 'Entre 22 cm y 26 cm',
          lengthCm: 32.5,
          widthCm: 24,
          heightCm: 1,
        },
        {
          value: 'OTRO',
          label: 'Otro (captura manual)',
          lengthRange: '',
          widthRange: '',
          lengthCm: null,
          widthCm: null,
          heightCm: null,
        },
      ],
      categories: [
        { value: 'DOCUMENTOS', label: 'Documentos' },
        { value: 'ELECTRONICOS', label: 'Electronicos' },
        { value: 'ROPA', label: 'Ropa' },
        { value: 'OTROS', label: 'Otros' },
      ],
    };
  },
  watch: {
    step(newStep) {
      if (newStep === this.detailsStep) {
        this.$nextTick(() => this.initOrUpdateTripMap());
      }
    },
    selectedTrip() {
      this.$nextTick(() => this.initOrUpdateTripMap());
    },
    deliveryLocation: {
      deep: true,
      handler() {
        this.$nextTick(() => this.initOrUpdateTripMap());
      },
    },
  },
  mounted() {
    const n = new Date();
    this.searchForm.fecha = `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, '0')}-${String(n.getDate()).padStart(2, '0')}`;
    this.applyDocumentPresetDimensions();
    this.fetchShippingRate();
    this.batchItems = [this.createEmptyBatchItem()];
  },
  beforeUnmount() {
    this.destroyTripMap();
  },
  methods: {
    createEmptyBatchItem() {
      return {
        localId: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        recipientName: '',
        recipientPhone: '',
        description: '',
        category: 'DOCUMENTOS',
        selectedDocumentType: 'CARTA',
        weightKg: 1,
        lengthCm: 29.2,
        widthCm: 23.8,
        heightCm: 1,
        declaredValueMxn: 0,
        urgency: false,
        specialInstructions: '',
        packagingConfirmed: false,
        noProhibitedConfirmed: false,
        offeredPriceMxn: null,
      };
    },
    addBatchItem() {
      this.batchItems.push(this.createEmptyBatchItem());
    },
    removeBatchItem(index) {
      if (this.batchItems.length <= 1) return;
      this.batchItems.splice(index, 1);
    },
    isDocumentCategory() {
      return this.form.category === 'DOCUMENTOS';
    },
    manualDimensionsEnabled() {
      return !this.isDocumentCategory() || this.selectedDocumentType === 'OTRO';
    },
    getSelectedDocumentPreset() {
      return this.documentSizePresets.find((preset) => preset.value === this.selectedDocumentType) || null;
    },
    applyDocumentPresetDimensions() {
      if (!this.isDocumentCategory()) return;
      const preset = this.getSelectedDocumentPreset();
      if (!preset || preset.value === 'OTRO') return;
      this.form.lengthCm = preset.lengthCm;
      this.form.widthCm = preset.widthCm;
      this.form.heightCm = preset.heightCm;
    },
    handleCategoryChange() {
      if (this.isDocumentCategory()) {
        if (!this.selectedDocumentType) this.selectedDocumentType = 'CARTA';
        this.applyDocumentPresetDimensions();
      }
    },
    handleDocumentTypeChange() {
      this.applyDocumentPresetDimensions();
    },
    isBatchDocumentCategory(item) {
      return item?.category === 'DOCUMENTOS';
    },
    batchManualDimensionsEnabled(item) {
      const docType = item?.selectedDocumentType || 'CARTA';
      return !this.isBatchDocumentCategory(item) || docType === 'OTRO';
    },
    getBatchItemDocumentPreset(item) {
      const key = item?.selectedDocumentType || 'CARTA';
      return this.documentSizePresets.find((p) => p.value === key) || null;
    },
    applyBatchItemDocumentPresetDimensions(item) {
      if (!this.isBatchDocumentCategory(item)) return;
      const preset = this.getBatchItemDocumentPreset(item);
      if (!preset || preset.value === 'OTRO') return;
      item.lengthCm = preset.lengthCm;
      item.widthCm = preset.widthCm;
      item.heightCm = preset.heightCm;
    },
    handleBatchItemCategoryChange(item) {
      if (this.isBatchDocumentCategory(item)) {
        if (!item.selectedDocumentType) item.selectedDocumentType = 'CARTA';
        this.applyBatchItemDocumentPresetDimensions(item);
      }
    },
    handleBatchItemDocumentTypeChange(item) {
      this.applyBatchItemDocumentPresetDimensions(item);
    },
    formatDate(d) {
      return d ? new Date(d).toLocaleDateString('es-MX') : '-';
    },
    formatTime(d) {
      return d ? new Date(d).toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' }) : '-';
    },
    normalizeTimePart(timeValue) {
      if (!timeValue) return null;
      if (typeof timeValue === 'string') {
        if (timeValue.includes('T')) {
          const parsed = new Date(timeValue);
          if (Number.isNaN(parsed.getTime())) return null;
          const hh = String(parsed.getHours()).padStart(2, '0');
          const mm = String(parsed.getMinutes()).padStart(2, '0');
          const ss = String(parsed.getSeconds()).padStart(2, '0');
          return `${hh}:${mm}:${ss}`;
        }
        const match = timeValue.match(/^(\d{2}):(\d{2})(?::(\d{2}))?/);
        if (match) {
          const hh = match[1];
          const mm = match[2];
          const ss = match[3] || '00';
          return `${hh}:${mm}:${ss}`;
        }
      }
      const parsed = new Date(timeValue);
      if (Number.isNaN(parsed.getTime())) return null;
      const hh = String(parsed.getHours()).padStart(2, '0');
      const mm = String(parsed.getMinutes()).padStart(2, '0');
      const ss = String(parsed.getSeconds()).padStart(2, '0');
      return `${hh}:${mm}:${ss}`;
    },
    getTripDateTime(trip) {
      const datePart = trip?.tripDate ? String(trip.tripDate).slice(0, 10) : null;
      const timePart = this.normalizeTimePart(trip?.tripTime);
      if (!datePart || !timePart) return null;
      const dt = new Date(`${datePart}T${timePart}`);
      return Number.isNaN(dt.getTime()) ? null : dt;
    },
    removePastTrips(items) {
      const now = new Date();
      return (items || []).filter((trip) => {
        const tripDateTime = this.getTripDateTime(trip);
        if (!tripDateTime) return true;
        return tripDateTime >= now;
      });
    },
    normalizePlaceForSearch(value) {
      if (!value) return '';
      const firstPart = value.split(',')[0] || value;
      return firstPart.trim().toLowerCase();
    },
    async buscarViajes() {
      this.loadingTrips = true;
      this.errorMsg = '';
      this.selectedTrip = null;
      try {
        const params = { page: 1, limit: 100, estado: 'ACTIVO' };
        if (this.searchForm.fecha) params.fecha = this.searchForm.fecha;

        const origenTerm = this.normalizePlaceForSearch(this.searchForm.origen);
        const destinoTerm = this.normalizePlaceForSearch(this.searchForm.destino);
        if (origenTerm) params.origen = origenTerm;
        if (destinoTerm) params.destino = destinoTerm;

        const { data } = await tripsApi.getAll(params);
        const items = this.removePastTrips(data?.items || []);

        // Fallback: si la combinación no trajo datos, intenta sin filtros de texto.
        if (items.length === 0 && (origenTerm || destinoTerm)) {
          const fallbackParams = { page: 1, limit: 100, estado: 'ACTIVO' };
          if (this.searchForm.fecha) fallbackParams.fecha = this.searchForm.fecha;
          const fallback = await tripsApi.getAll(fallbackParams);
          this.availableTrips = this.removePastTrips(fallback?.data?.items || []);
        } else {
          this.availableTrips = items;
        }
      } catch (e) {
        const msg = e.response?.data?.message || 'No se pudieron cargar los viajes.';
        this.errorMsg = Array.isArray(msg) ? msg.join(' ') : msg;
        this.availableTrips = [];
      } finally {
        this.loadingTrips = false;
      }
    },
    prefillDeliveryLocationFromTripIfEmpty() {
      if (!this.selectedTrip) return;
      if (!this.selectedTrip.deliveryFlexible) return;
      if (this.hasDeliveryLocation) return;

      const lat = Number(this.selectedTrip.destinationLatitude);
      const lng = Number(this.selectedTrip.destinationLongitude);
      const addr = (this.selectedTrip.destinationAddress || '').trim();
      if (!Number.isFinite(lat) || !Number.isFinite(lng) || !addr) return;

      this.deliveryLocation = { latitude: lat, longitude: lng, address: addr };
    },
    destroyTripMap() {
      if (Array.isArray(this.tripMarkers)) {
        this.tripMarkers.forEach((m) => {
          try {
            m?.remove?.();
          } catch {
            // ignore
          }
        });
      }
      this.tripMarkers = [];
      if (this.tripMap) {
        try {
          this.tripMap.remove();
        } catch {
          // ignore
        }
      }
      this.tripMap = null;
    },
    makeImageMarkerEl(src, alt) {
      const el = document.createElement('div');
      el.className = 'trip-marker';
      el.style.width = '28px';
      el.style.height = '28px';
      el.style.display = 'flex';
      el.style.alignItems = 'center';
      el.style.justifyContent = 'center';
      const img = document.createElement('img');
      img.src = src;
      img.alt = alt || 'marker';
      img.className = 'trip-marker-img';
      img.style.width = '28px';
      img.style.height = '28px';
      img.style.objectFit = 'contain';
      img.style.display = 'block';
      el.appendChild(img);
      return el;
    },
    makeGreenDeliveryMarkerEl() {
      const el = document.createElement('div');
      el.className = 'trip-marker trip-marker-delivery';
      el.style.width = '14px';
      el.style.height = '14px';
      el.style.background = '#198754';
      el.style.border = '3px solid #ffffff';
      el.style.borderRadius = '50%';
      el.style.boxShadow = '0 6px 16px rgba(25, 135, 84, 0.35)';
      return el;
    },
    getTripMapPoints() {
      if (!this.selectedTrip) return [];
      const points = [];
      const oLat = Number(this.selectedTrip.originLatitude);
      const oLng = Number(this.selectedTrip.originLongitude);
      const dLat = Number(this.selectedTrip.destinationLatitude);
      const dLng = Number(this.selectedTrip.destinationLongitude);
      if (Number.isFinite(oLat) && Number.isFinite(oLng)) {
        points.push({ type: 'origin', lat: oLat, lng: oLng });
      }
      if (Number.isFinite(dLat) && Number.isFinite(dLng)) {
        points.push({ type: 'destination', lat: dLat, lng: dLng });
      }
      if (this.isFlexibleTrip && this.hasDeliveryLocation) {
        points.push({
          type: 'delivery',
          lat: Number(this.deliveryLocation.latitude),
          lng: Number(this.deliveryLocation.longitude),
        });
      }
      return points;
    },
    initOrUpdateTripMap() {
      if (this.step !== this.detailsStep) return;
      if (!this.selectedTrip) return;
      const mapboxgl = window.mapboxgl;
      const token = process.env.VUE_APP_MAPBOX_TOKEN || '';
      const container = this.$refs.tripMapContainer;
      if (!container || !mapboxgl || !token) return;

      mapboxgl.accessToken = token;
      const points = this.getTripMapPoints();
      const fallbackLng = Number(this.selectedTrip.destinationLongitude) || Number(this.selectedTrip.originLongitude) || -102.5528;
      const fallbackLat = Number(this.selectedTrip.destinationLatitude) || Number(this.selectedTrip.originLatitude) || 23.6345;

      if (!this.tripMap) {
        this.tripMap = new mapboxgl.Map({
          container,
          style: 'mapbox://styles/mapbox/streets-v12',
          center: [fallbackLng, fallbackLat],
          zoom: 10,
          interactive: true,
        });
        this.tripMap.on('load', () => {
          this.initOrUpdateTripMap();
        });
        return;
      }

      // Clear previous markers
      if (Array.isArray(this.tripMarkers)) {
        this.tripMarkers.forEach((m) => m?.remove?.());
      }
      this.tripMarkers = [];

      // Add markers
      points.forEach((p) => {
        let el = null;
        if (p.type === 'origin') el = this.makeImageMarkerEl(origenMarkerImg, 'origen');
        if (p.type === 'destination') el = this.makeImageMarkerEl(destinoMarkerImg, 'destino');
        if (p.type === 'delivery') el = this.makeGreenDeliveryMarkerEl();
        if (!el) return;
        const marker = new mapboxgl.Marker({ element: el, anchor: 'bottom' })
          .setLngLat([p.lng, p.lat])
          .addTo(this.tripMap);
        this.tripMarkers.push(marker);
      });

      // Fit bounds
      if (points.length >= 2) {
        const bounds = new mapboxgl.LngLatBounds();
        points.forEach((p) => bounds.extend([p.lng, p.lat]));
        this.tripMap.fitBounds(bounds, { padding: 40, duration: 500, maxZoom: 14 });
      } else if (points.length === 1) {
        this.tripMap.flyTo({ center: [points[0].lng, points[0].lat], zoom: 13, duration: 500 });
      }
    },
    goToNextAfterTripSelect() {
      if (!this.selectedTrip) return;
      if (this.selectedTrip.deliveryFlexible) {
        this.prefillDeliveryLocationFromTripIfEmpty();
        this.step = 3;
        return;
      }
      this.step = this.detailsStep;
    },
    async handleSearchStep() {
      await this.buscarViajes();
      this.step = 2;
    },
    selectTrip(trip) {
      this.selectedTrip = trip;
      this.deliveryLocation = { latitude: null, longitude: null, address: '' };
    },
    onConfirmDeliveryLocation(loc) {
      this.deliveryLocation = {
        latitude: Number(loc.latitude),
        longitude: Number(loc.longitude),
        address: (loc.address || '').trim(),
      };
    },
    toRad(deg) {
      return (deg * Math.PI) / 180;
    },
    estimateDistanceKm() {
      if (!this.selectedTrip) return 0;
      const lat1 = Number(this.selectedTrip.originLatitude);
      const lon1 = Number(this.selectedTrip.originLongitude);
      const lat2 = Number(this.selectedTrip.destinationLatitude);
      const lon2 = Number(this.selectedTrip.destinationLongitude);
      if (![lat1, lon1, lat2, lon2].every((v) => Number.isFinite(v))) return 0;
      const dLat = this.toRad(lat2 - lat1);
      const dLon = this.toRad(lon2 - lon1);
      const a =
        Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.cos(this.toRad(lat1)) *
          Math.cos(this.toRad(lat2)) *
          Math.sin(dLon / 2) *
          Math.sin(dLon / 2);
      const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
      return 6371 * c;
    },
    calculateEstimatedShipping() {
      const distanceKm = this.estimateDistanceKm();
      const volume = Number(this.form.lengthCm || 0) * Number(this.form.widthCm || 0) * Number(this.form.heightCm || 0);
      const weight = Number(this.form.weightKg || 0);
      const rate = this.shippingRate;
      const estimated =
        Number(rate.baseFare || 0) +
        distanceKm * Number(rate.perKm || 0) +
        weight * Number(rate.perKg || 0) +
        volume * Number(rate.perVolumeFactor || 0) +
        (this.form.urgency ? Number(rate.urgencySurcharge || 0) : 0);
      return Number(estimated.toFixed(2));
    },
    async confirmEstimatedPriceModal() {
      const estimated = this.calculateEstimatedShipping();
      const flexibleNote = this.selectedTrip?.deliveryFlexible
        ? `<p style="margin:0 0 10px;color:#6c757d;">Este viaje permite <strong>entrega personalizada</strong>. Ofrecer un precio justo aumenta la probabilidad de que el viajero acepte tu paquete.</p>`
        : '';
      const result = await Swal.fire({
        title: 'Tarifa de envio estimada',
        html: `
          ${flexibleNote}
          <p style="margin-bottom:8px;">Tarifa sugerida (referencia): <strong>$${estimated.toFixed(2)} MXN</strong></p>
          <p style="margin-bottom:10px;">Puedes modificar este valor antes de publicar.</p>
          <input id="offeredPriceInput" type="number" min="0" step="0.01" class="swal2-input" value="${this.form.offeredPriceMxn ?? estimated}" placeholder="Precio a publicar (MXN)" />
        `,
        showCancelButton: true,
        confirmButtonText: 'Publicar paquete',
        cancelButtonText: 'Cancelar',
        confirmButtonColor: '#0d6efd',
        preConfirm: () => {
          const input = document.getElementById('offeredPriceInput');
          const value = Number(input?.value);
          if (!Number.isFinite(value) || value < 0) {
            Swal.showValidationMessage('Ingresa un precio valido mayor o igual a 0.');
            return null;
          }
          return value;
        },
      });
      if (!result.isConfirmed) return null;
      return {
        estimated,
        offeredPrice: result.value,
      };
    },
    async confirmEstimatedPriceModalForBatchItem(item, index, total) {
      const estimated = this.calculateEstimatedShippingFromValues(item);
      const flexibleNote = this.selectedTrip?.deliveryFlexible
        ? `<p style="margin:0 0 10px;color:#6c757d;">Este viaje permite <strong>entrega personalizada</strong>. Ofrecer un precio justo aumenta la probabilidad de aceptación.</p>`
        : '';
      const rawInitial = item.offeredPriceMxn;
      const initial =
        rawInitial != null && rawInitial !== '' && Number.isFinite(Number(rawInitial))
          ? Number(rawInitial)
          : estimated;
      const isLast = index === total - 1;
      const result = await Swal.fire({
        title: 'Tarifa de envio estimada',
        html: `
          <p style="margin-bottom:6px;font-size:0.9rem;color:#6c757d;">Paquete ${index + 1} de ${total}</p>
          ${flexibleNote}
          <p style="margin-bottom:8px;">Tarifa sugerida (referencia): <strong>$${estimated.toFixed(2)} MXN</strong></p>
          <p style="margin-bottom:10px;">Puedes modificar este valor antes de publicar.</p>
          <input id="offeredPriceInputBatch" type="number" min="0" step="0.01" class="swal2-input" value="${initial}" placeholder="Precio a publicar (MXN)" />
        `,
        showCancelButton: true,
        confirmButtonText: isLast ? 'Publicar lote' : 'Siguiente',
        cancelButtonText: 'Cancelar',
        confirmButtonColor: '#0d6efd',
        preConfirm: () => {
          const input = document.getElementById('offeredPriceInputBatch');
          const value = Number(input?.value);
          if (!Number.isFinite(value) || value < 0) {
            Swal.showValidationMessage('Ingresa un precio valido mayor o igual a 0.');
            return null;
          }
          return value;
        },
      });
      if (!result.isConfirmed) return null;
      return {
        estimated,
        offeredPrice: result.value,
      };
    },
    async onSubmit() {
      if (this.createMode === 'batch') {
        await this.onSubmitBatch();
        return;
      }
      await this.onSubmitSingle();
    },
    async onSubmitSingle() {
      this.loading = true;
      this.errorMsg = '';
      this.successMsg = '';
      try {
        if (!this.selectedTrip?.id) throw new Error('Debes seleccionar un viaje publicado.');
        if (this.isFlexibleTrip && !this.hasDeliveryLocation) {
          throw new Error('Selecciona la dirección de entrega personalizada.');
        }
        if (!this.form.packagingConfirmed || !this.form.noProhibitedConfirmed) {
          throw new Error('Debes confirmar empaque adecuado y que no contiene artículos prohibidos.');
        }
        const pricing = await this.confirmEstimatedPriceModal();
        if (!pricing) {
          this.loading = false;
          return;
        }
        await packagesApi.create({
          ...this.form,
          tripId: this.selectedTrip.id,
          deliveryAddress: this.isFlexibleTrip ? this.deliveryLocation.address : undefined,
          deliveryLatitude: this.isFlexibleTrip ? this.deliveryLocation.latitude : undefined,
          deliveryLongitude: this.isFlexibleTrip ? this.deliveryLocation.longitude : undefined,
          offeredPriceMxn: pricing.offeredPrice,
          specialInstructions:
            this.form.specialInstructions && this.form.specialInstructions.trim().length >= 3
              ? this.form.specialInstructions.trim()
              : undefined,
          recipientPhone: this.form.recipientPhone?.trim() || undefined,
        });
        this.successMsg = 'Paquete registrado correctamente.';
        await Swal.fire({ icon: 'success', title: 'Paquete creado', text: this.successMsg, confirmButtonColor: '#0d6efd' });
      } catch (e) {
        const msg = e.response?.data?.message || e.message || 'No se pudo crear el paquete.';
        this.errorMsg = Array.isArray(msg) ? msg.join(' ') : msg;
      } finally {
        this.loading = false;
      }
    },
    async onSubmitBatch() {
      this.loading = true;
      this.errorMsg = '';
      this.successMsg = '';
      try {
        if (!this.selectedTrip?.id) throw new Error('Debes seleccionar un viaje publicado.');
        if (this.isFlexibleTrip && !this.hasDeliveryLocation) {
          throw new Error('Selecciona la dirección de entrega personalizada.');
        }
        if (this.batchSameRecipient && !this.batchCommonRecipientName?.trim()) {
          throw new Error('Debes indicar el destinatario común.');
        }
        const total = this.batchItems.length;
        for (let idx = 0; idx < total; idx += 1) {
          const item = this.batchItems[idx];
          if (!this.batchSameRecipient && !item.recipientName?.trim()) {
            throw new Error(`Falta destinatario en el paquete #${idx + 1}.`);
          }
          if (!item.packagingConfirmed || !item.noProhibitedConfirmed) {
            throw new Error(`Debes confirmar empaque/prohibidos en el paquete #${idx + 1}.`);
          }
        }

        const offeredPrices = [];
        for (let idx = 0; idx < total; idx += 1) {
          const item = this.batchItems[idx];
          const pricing = await this.confirmEstimatedPriceModalForBatchItem(item, idx, total);
          if (!pricing) {
            this.loading = false;
            return;
          }
          offeredPrices.push(pricing.offeredPrice);
        }

        const items = this.batchItems.map((item, idx) => ({
          recipientName: this.batchSameRecipient
            ? this.batchCommonRecipientName.trim()
            : item.recipientName.trim(),
          recipientPhone: this.batchSameRecipient
            ? this.batchCommonRecipientPhone?.trim() || undefined
            : item.recipientPhone?.trim() || undefined,
          description: item.description,
          category: item.category,
          weightKg: Number(item.weightKg),
          lengthCm: Number(item.lengthCm),
          widthCm: Number(item.widthCm),
          heightCm: Number(item.heightCm),
          tripId: this.selectedTrip.id,
          deliveryAddress: this.isFlexibleTrip ? this.deliveryLocation.address : undefined,
          deliveryLatitude: this.isFlexibleTrip ? this.deliveryLocation.latitude : undefined,
          deliveryLongitude: this.isFlexibleTrip ? this.deliveryLocation.longitude : undefined,
          declaredValueMxn: Number(item.declaredValueMxn || 0),
          urgency: Boolean(item.urgency),
          specialInstructions:
            item.specialInstructions && item.specialInstructions.trim().length >= 3
              ? item.specialInstructions.trim()
              : undefined,
          packagingConfirmed: true,
          noProhibitedConfirmed: true,
          offeredPriceMxn: Number(offeredPrices[idx]),
        }));
        await packagesApi.createBatch({
          sameRecipient: this.batchSameRecipient,
          commonRecipientName: this.batchSameRecipient
            ? this.batchCommonRecipientName.trim()
            : undefined,
          commonRecipientPhone: this.batchSameRecipient
            ? this.batchCommonRecipientPhone?.trim() || undefined
            : undefined,
          items,
        });
        this.successMsg = `${items.length} paquetes publicados correctamente.`;
        await Swal.fire({
          icon: 'success',
          title: 'Lote publicado',
          text: this.successMsg,
          confirmButtonColor: '#0d6efd',
        });
        this.batchItems = [this.createEmptyBatchItem()];
      } catch (e) {
        const msg = e.response?.data?.message || e.message || 'No se pudo publicar el lote.';
        this.errorMsg = Array.isArray(msg) ? msg.join(' ') : msg;
      } finally {
        this.loading = false;
      }
    },
    calculateEstimatedShippingFromValues(values) {
      const distanceKm = this.estimateDistanceKm();
      const volume =
        Number(values.lengthCm || 0) *
        Number(values.widthCm || 0) *
        Number(values.heightCm || 0);
      const weight = Number(values.weightKg || 0);
      const rate = this.shippingRate;
      const estimated =
        Number(rate.baseFare || 0) +
        distanceKm * Number(rate.perKm || 0) +
        weight * Number(rate.perKg || 0) +
        volume * Number(rate.perVolumeFactor || 0) +
        (values.urgency ? Number(rate.urgencySurcharge || 0) : 0);
      return Number(estimated.toFixed(2));
    },
    async fetchShippingRate() {
      try {
        const { data } = await shippingRatesApi.getActive();
        if (data) {
          this.shippingRate = {
            baseFare: Number(data.baseFare ?? this.shippingRate.baseFare),
            perKm: Number(data.perKm ?? this.shippingRate.perKm),
            perKg: Number(data.perKg ?? this.shippingRate.perKg),
            perVolumeFactor: Number(data.perVolumeFactor ?? this.shippingRate.perVolumeFactor),
            urgencySurcharge: Number(data.urgencySurcharge ?? this.shippingRate.urgencySurcharge),
          };
        }
      } catch {
        // Keep defaults if configuration API is unavailable.
      }
    },
    async showForbiddenObjects() {
      try {
        const { data } = await forbiddenObjectsApi.getAll();
        const rows = (data || [])
          .map((item) => `<tr><td>${item.name}</td><td>${item.description || '-'}</td></tr>`)
          .join('');
        await Swal.fire({
          title: 'Objetos prohibidos',
          width: 700,
          html: `<div style="max-height:320px;overflow:auto;"><table style="width:100%;text-align:left;border-collapse:collapse;"><thead><tr><th>Objeto</th><th>Descripción</th></tr></thead><tbody>${rows || '<tr><td colspan="2">No hay objetos configurados.</td></tr>'}</tbody></table></div>`,
          confirmButtonColor: '#0d6efd',
        });
      } catch {
        await Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'No se pudo cargar la lista de objetos prohibidos.',
          confirmButtonColor: '#0d6efd',
        });
      }
    },
    onAddressInput(field) {
      if (this.addressDebounceTimer) clearTimeout(this.addressDebounceTimer);
      this.addressDebounceTimer = setTimeout(() => this.fetchAddressSuggestions(field), 350);
    },
    async fetchAddressSuggestions(field) {
      const token = process.env.VUE_APP_MAPBOX_TOKEN || '';
      if (!token) return;
      const q = (field === 'origen' ? this.searchForm.origen : this.searchForm.destino || '').trim();
      if (q.length < 3) return;
      const params = new URLSearchParams({ access_token: token, language: 'es', country: 'mx', autocomplete: 'true', limit: '5' });
      const resp = await fetch(`https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(q)}.json?${params.toString()}`);
      if (!resp.ok) return;
      const features = (await resp.json())?.features || [];
      if (field === 'origen') {
        this.originSuggestions = features;
        this.showOrigenSuggestions = true;
      } else {
        this.destinationSuggestions = features;
        this.showDestinoSuggestions = true;
      }
    },
    selectSuggestion(field, item) {
      if (field === 'origen') {
        this.searchForm.origen = item.place_name;
        this.showOrigenSuggestions = false;
      } else {
        this.searchForm.destino = item.place_name;
        this.showDestinoSuggestions = false;
      }
    },
  },
  computed: {
    isFlexibleTrip() {
      return Boolean(this.selectedTrip?.deliveryFlexible);
    },
    detailsStep() {
      return this.isFlexibleTrip ? 4 : 3;
    },
    hasDeliveryLocation() {
      const lat = Number(this.deliveryLocation?.latitude);
      const lng = Number(this.deliveryLocation?.longitude);
      const addr = (this.deliveryLocation?.address || '').trim();
      return Number.isFinite(lat) && Number.isFinite(lng) && addr.length >= 3;
    },
    selectedDocumentPreset() {
      return this.getSelectedDocumentPreset();
    },
  },
};
</script>

<style scoped>
.wizard-steps { display: grid; grid-template-columns: repeat(3, 1fr); gap: 10px; }
.wizard-step { border: 1px solid #dce3f4; border-radius: 10px; padding: 10px; text-align: center; font-size: 0.9rem; color: #6a7894; background: #f8faff; }
.wizard-step.active { border-color: #0d6efd; color: #0d3b8f; background: #ecf4ff; font-weight: 700; }
.wizard-step.done { border-color: #60b383; color: #237046; background: #edf8f1; }
.trip-cards { display: grid; gap: 10px; }
.trip-card { border: 1px solid #dce3f4; border-radius: 12px; background: #fff; padding: 12px; text-align: left; }
.trip-card.selected { border-color: #0d6efd; box-shadow: 0 0 0 3px rgba(13, 110, 253, 0.1); }
.suggestions-list { max-height: 190px; overflow: auto; border: 1px solid #dee3f1; border-radius: 10px; background: #fff; }
.suggestion-item { border: 0; background: #fff; width: 100%; text-align: left; padding: 8px 10px; border-bottom: 1px solid #f1f3f8; }
.suggestion-item:hover { background: #f7fbff; }

.trip-map {
  width: 100%;
  height: 260px;
  border-radius: 12px;
  border: 1px solid #dce3f4;
  overflow: hidden;
  margin-top: 12px;
}
.trip-marker {
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.trip-marker-img {
  width: 28px;
  height: 28px;
  object-fit: contain;
  display: block;
}
.trip-marker-delivery {
  width: 28px;
  height: 28px;
  background : #198754;
  border: 3px solid #ffffff;
  border-radius: 50%;
  box-shadow: 0 6px 16px rgba(25, 135, 84, 0.35);
}
</style>
